import { render } from 'preact';

import { namespace } from '../helpers/namespaces';
import { determineTenant } from '../helpers/client';
import { hexToRGB } from '../helpers/colours';
import { applyThemeFromEmbed } from '../helpers/theme-config';
import Poll from './poll.component';
import TopThreePoll from '../components/TopThree/TopThreePoll.component';

import { getAuthHeader } from '../helpers/authentication';
import { getPollData } from './apiRequests';

import { DataCaptureForm as Form } from 'playbook-data-capture-sdk-web';
import { POLL_TYPES } from '../helpers/constants';
import { I18nextProvider } from 'react-i18next';
import i18nInstance from '../i18n';

const ID_DATA_ATTRIBUTE = `data-${namespace}poll`;
const ID_DATA_CAPTURE_FORM = `data-${namespace}form`;
const ID_DATA_ATTRIBUTE_TRIVIA = `data-${namespace}trivia`;
const TENANT_DATA_ATTRIBUTE = `data-${namespace}tenant`;

const PREVIEW_DATA_ATTRIBUTE = `${ID_DATA_ATTRIBUTE}-preview`;
const INIT_ATTR = 'data-fek-initialised';
const NO_INIT_SELECTOR = `:not([${INIT_ATTR}])`;

/**
 * Class which goes through page and finds FEK polls to embed
 */
export default class Polls {
    constructor() {
        this.init();
        this.observeDOM();
    }

    init() {
        document
            .querySelectorAll(`[${ID_DATA_ATTRIBUTE}]${NO_INIT_SELECTOR}`)
            .forEach((item) => this.initNewPoll(item, false));

        document
            .querySelectorAll(
                `[${ID_DATA_ATTRIBUTE_TRIVIA}]${NO_INIT_SELECTOR}`
            )
            .forEach((item) => this.initNewPoll(item, true));
    }

    initNewPoll(pollEmbed, isTrivia = false) {
        const embedTenant = pollEmbed.getAttribute(TENANT_DATA_ATTRIBUTE);
        determineTenant(embedTenant);

        const pollId = isTrivia
            ? pollEmbed.getAttribute(ID_DATA_ATTRIBUTE_TRIVIA)
            : pollEmbed.getAttribute(ID_DATA_ATTRIBUTE);
        pollEmbed.setAttribute(INIT_ATTR, '');

        getPollData(pollId, isTrivia).then((pollData) => {
            if (!pollData) {
                return;
            }
            const authHeader = getAuthHeader();
            if (pollData?.gated && authHeader) {
                getPollData(pollId, isTrivia, true, authHeader).then(
                    (gatedPollData) => {
                        if (!gatedPollData) {
                            return;
                        }
                        this.renderEmbed(pollEmbed, gatedPollData, false, null);
                    }
                );
            } else {
                this.renderEmbed(pollEmbed, pollData, false, null);
            }
        });
    }

    initPreviews(onPreviewEditChange) {
        document
            .querySelectorAll(`[${PREVIEW_DATA_ATTRIBUTE}]`)

            .forEach((pollPreviewEmbed) => {
                const pollData = JSON.parse(
                    pollPreviewEmbed.getAttribute(PREVIEW_DATA_ATTRIBUTE)
                );
                this.renderEmbed(
                    pollPreviewEmbed,
                    pollData,
                    true,
                    onPreviewEditChange
                );
            });
    }

    /**
     * Sets up a MutationObserver to check the DOM
     * if new poll embeds have been dynamically added
     */
    observeDOM() {
        const observer = new MutationObserver((mutationList) => {
            mutationList.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        const triviaEmbed = node.querySelector(
                            `[${ID_DATA_ATTRIBUTE_TRIVIA}]${NO_INIT_SELECTOR}`
                        );

                        const pollEmbed = node.querySelector(
                            `[${ID_DATA_ATTRIBUTE}]${NO_INIT_SELECTOR}`
                        );
                        if (pollEmbed) {
                            this.initNewPoll(pollEmbed);
                        }

                        if (triviaEmbed) {
                            this.initNewPoll(triviaEmbed, true);
                        }
                    }
                });
            });
        });

        observer.observe(document.body, { subtree: true, childList: true });
    }

    /**
     * Renders embeds to DOM
     *
     * @param {Element} container - embed container
     * @param {object} pollData - poll data
     * @param {boolean} isPreview - poll embed is a preview
     * @param {Function} onPreviewEditChange callback function when contenteditable data changes
     */
    async renderEmbed(container, pollData, isPreview, onPreviewEditChange) {
        applyThemeFromEmbed(container);

        /**
         * Used to fetch poll data to get latest vote data
         *
         * @returns {Promise} fetch promise
         */

        // Check if there is a linked form
        const embeddedFormId =
            container.firstElementChild?.getAttribute(ID_DATA_CAPTURE_FORM);
        let embeddedFormData;
        if (embeddedFormId) {
            embeddedFormData = await Form.initEmbeddableNewForm(embeddedFormId)
                .then((formData) => formData)
                .catch((rejectValue) => rejectValue);
        }

        const refetch = (pollData) => {
            const isTrivia =
                (pollData?.type === POLL_TYPES.TRIVIA ||
                    pollData?.type === POLL_TYPES.FREE_TEXT) ??
                false;
            const isGated = pollData?.gated === true;
            const authHeader = isGated ? getAuthHeader() : null;
            return getPollData(
                isTrivia ? pollData.trivia_id : pollData.poll_id,
                isTrivia,
                isGated,
                authHeader
            );
        };

        // Set BG colour and gradient overrides.
        const hexBgColour = pollData?.background_colour;
        const rgbBgColour = hexBgColour ? hexToRGB(hexBgColour) : null;

        const colourOverrides = {
            hex: hexBgColour,
            rgb: rgbBgColour
        };

        const expFeatureFlags = window.PLAYBOOK_CONFIG?.features || {};
        const shadow =
            container.shadowRoot || container.attachShadow({ mode: 'open' });

        render(
            <I18nextProvider i18n={i18nInstance}>
                {pollData.type === POLL_TYPES.TOP_THREE ? (
                    <TopThreePoll
                        pollData={pollData}
                        embeddedFormData={embeddedFormData}
                        container={container}
                        refetch={() => refetch(pollData)}
                        isPreview={isPreview}
                        onPreviewEditChange={onPreviewEditChange}
                        colourOverrides={colourOverrides}
                    />
                ) : (
                    <Poll
                        pollData={pollData}
                        embeddedFormData={embeddedFormData}
                        container={container}
                        refetch={() => refetch(pollData)}
                        isPreview={isPreview}
                        onPreviewEditChange={onPreviewEditChange}
                        expFeatureFlags={expFeatureFlags}
                        colourOverrides={colourOverrides}
                    />
                )}
            </I18nextProvider>,
            shadow
        );
    }
}
